import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Brain, Zap } from 'lucide-react';
import { articlesApi } from '../../lib/api';

const READING_HISTORY_KEY = 'ninja_reading_history';
const MAX_HISTORY = 50;

function getReadingHistory() {
  try {
    return JSON.parse(localStorage.getItem(READING_HISTORY_KEY) || '[]');
  } catch { return []; }
}

function addToReadingHistory(slug) {
  const history = getReadingHistory().filter(s => s !== slug);
  history.unshift(slug);
  localStorage.setItem(READING_HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

export const AIRecommendations = ({ currentSlug, category }) => {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');

  const readHistory = useMemo(() => getReadingHistory(), []);

  useEffect(() => {
    if (currentSlug) addToReadingHistory(currentSlug);
  }, [currentSlug]);

  useEffect(() => {
    if (!currentSlug) return;
    let cancelled = false;

    const fetchRecs = async () => {
      setLoading(true);
      try {
        const exclude = [...new Set([currentSlug, ...readHistory.slice(0, 10)])].join(',');
        const res = await articlesApi.getAiRecommendations(currentSlug, exclude, 4);
        if (!cancelled) {
          setRecs(res.data?.recommendations || []);
          setSource(res.data?.source || 'fallback');
        }
      } catch {
        if (!cancelled) setRecs([]);
      }
      if (!cancelled) setLoading(false);
    };
    fetchRecs();

    return () => { cancelled = true; };
  }, [currentSlug, readHistory]);

  if (loading) {
    return (
      <div className="mt-12 pt-10 border-t border-zinc-800/60" data-testid="ai-recommendations-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00F0FF]/20 to-purple-500/20 flex items-center justify-center">
            <Brain className="w-4 h-4 text-[#00F0FF]" />
          </div>
          <h3 className="text-xl text-white font-heading">AI Picks For You</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex gap-4 p-4 bg-zinc-900/30 border border-zinc-800/50 animate-pulse">
              <div className="w-24 h-24 bg-zinc-800 flex-shrink-0" />
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                <div className="h-3 bg-zinc-800 rounded w-1/2" />
                <div className="h-3 bg-zinc-800 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recs.length === 0) return null;

  const isAI = source === 'ai' || source === 'ai_cached';

  return (
    <div className="mt-12 pt-10 border-t border-zinc-800/60" data-testid="ai-recommendations-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00F0FF]/20 to-purple-500/20 flex items-center justify-center">
            <Brain className="w-4 h-4 text-[#00F0FF]" />
          </div>
          <h3 className="text-xl text-white font-heading">AI Picks For You</h3>
          {isAI && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20 rounded-full flex items-center gap-1">
              <Zap className="w-2.5 h-2.5" /> AI Curated
            </span>
          )}
        </div>
        <span className="text-[10px] text-zinc-600 uppercase tracking-widest hidden sm:block">
          {readHistory.length > 2 ? 'Personalized' : 'Based on this article'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {recs.map((article, idx) => (
          <Link
            key={article.slug}
            to={`/blogs/article/${article.slug}`}
            className="group flex gap-4 p-4 bg-zinc-900/20 border border-zinc-800/40 hover:border-[#00F0FF]/30 hover:bg-zinc-900/40 transition-all duration-300"
            data-testid={`ai-rec-${article.slug}`}
            style={{ animationDelay: `${idx * 80}ms` }}
          >
            <div className="w-24 h-24 flex-shrink-0 overflow-hidden bg-zinc-800">
              {article.featured_image && (
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
              <div>
                <span className="text-[9px] uppercase tracking-widest text-[#E63946] font-bold">
                  {article.category}
                </span>
                <h4 className="text-white text-sm font-heading leading-tight line-clamp-2 mt-1 group-hover:text-[#00F0FF] transition-colors duration-300">
                  {article.title}
                </h4>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1.5 text-zinc-600 text-[11px]">
                  <Clock className="w-3 h-3" />
                  <span>{article.read_time || 5} min</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-[#00F0FF] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AIRecommendations;
